import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { X } from "lucide-react";
import { splitAddresses } from "@/lib/addresses";

/**
 * Gmail-style chip recipient input.
 *  - `value` is still a comma-separated string for backend compat.
 *  - Committed addresses render as removable pill-chips with the recipient's
 *    first-letter avatar; the trailing typed text is a plain input.
 *  - Hitting Enter, Tab, Space, comma, or selecting an autocomplete
 *    suggestion commits the current token to a chip.
 *  - Backspace on an empty input pops the last chip back into the input.
 *  - Iter 70 — Paste-explode: pasting a string with multiple
 *    comma/semicolon/space/<bracket>-delimited addresses splits them all
 *    into individual chips at once.
 *  - Iter 70 — Regex email validation. Invalid tokens still render as
 *    chips so the user sees them, but in red so they can fix or delete.
 *  - Iter 110p — `parseAddresses` and `explodePastedString` both delegate
 *    to the shared `splitAddresses` parser which respects "Last, First"
 *    quoted display names. The previous naïve split broke chips when
 *    the CC header used the Outlook last-name-first convention.
 */
// RFC-5322-lite regex. Doesn't try to validate every IDN edge case (Gmail
// will reject anything malformed on send anyway); this is just enough to
// surface obvious typos before the user clicks Send.
const EMAIL_RE = /^[^\s@<>"',;:]+@[^\s@<>"',;:]+\.[^\s@<>"',;:]+$/;

function parseAddresses(value) {
  return splitAddresses(value);
}
function joinAddresses(parts) {
  return parts.join(", ");
}
// Pull "Name <email>" → { name, email } or fall back to bare email.
function parseChip(token) {
  const m = token.match(/^(.*)<([^>]+)>\s*$/);
  if (m) {
    // Iter 110p — Strip the surrounding `"…"` from the display name
    // (Outlook's "Last, First" convention always wraps names in quotes).
    // The email portion is unaffected; this is purely a chip-label
    // cosmetic so `"Voutourlis, Andreas"` renders as `Voutourlis, Andreas`.
    let name = m[1].trim();
    if (name.length >= 2 && name.startsWith('"') && name.endsWith('"')) {
      name = name.slice(1, -1).trim();
    }
    return { name, email: m[2].trim() };
  }
  return { name: "", email: token.trim() };
}
function formatChip({ name, email }) {
  if (name) return `${name} <${email}>`;
  return email;
}
function isValidEmail(token) {
  const { email } = parseChip(token);
  return EMAIL_RE.test(email);
}
// Iter 70 — Paste-explode. Splits on commas, semicolons, newlines, tabs,
// and runs of whitespace. Also recognises angle-bracketed "Name <addr>"
// pairs and keeps them intact (the bracket itself isn't a delimiter).
//
// Iter 110p — Now delegates to the shared `splitAddresses` parser with
// `normaliseWhitespace=true` so pasted multi-line / tab-delimited input
// still works, but commas inside "Last, First" quoted display names no
// longer split the entry.
function explodePastedString(raw) {
  return splitAddresses(raw, { normaliseWhitespace: true });
}

function Chip({ token, onRemove, testId }) {
  const { name, email } = parseChip(token);
  const display = name ? `${name} (${email})` : email;
  const initial = ((name || email).trim()[0] || "?").toUpperCase();
  // Iter 70 — Tint chip red if the address fails basic validation so the
  // user gets immediate feedback (Gmail uses the same red underline).
  const valid = isValidEmail(token);
  const chipCls = valid
    ? "bg-slate-100 hover:bg-slate-200 border-slate-200 text-slate-800"
    : "bg-rose-50 hover:bg-rose-100 border-rose-300 text-rose-800";
  return (
    <span
      data-testid={testId}
      title={valid ? email : `Invalid address: ${email}`}
      className={`inline-flex items-center gap-1.5 border rounded-full pl-0.5 pr-1.5 py-0.5 text-[12px] max-w-full ${chipCls}`}
    >
      <span className={`h-5 w-5 shrink-0 rounded-full ${valid ? "bg-slate-700" : "bg-rose-700"} text-white grid place-items-center text-[10px] font-medium`}>
        {initial}
      </span>
      <span className="truncate max-w-[260px]" title={email}>{display}</span>
      <button
        type="button"
        onClick={onRemove}
        className={`shrink-0 -mr-0.5 ${valid ? "text-slate-400 hover:text-rose-700" : "text-rose-500 hover:text-rose-900"}`}
        data-testid={`${testId}-remove`}
        aria-label="Remove recipient"
      ><X className="h-3 w-3"/></button>
    </span>
  );
}

export default function AddressBookInput({
  value, onChange, placeholder = "name or email…", dataTestId,
}) {
  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const chips = useMemo(() => parseAddresses(value), [value]);

  const setChips = (next) => onChange(joinAddresses(next));

  const fetchSuggestions = useCallback(async (q) => {
    try {
      const r = await api.get(`/address-book?q=${encodeURIComponent(q)}`);
      setSuggestions(r.data || []);
      setOpen(true);
      setHighlighted(0);
    } catch (_) { /* silent */ }
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!draft || draft.length < 1) { setSuggestions([]); setOpen(false); return; }
    debounceRef.current = setTimeout(() => fetchSuggestions(draft), 200);
    return () => clearTimeout(debounceRef.current);
  }, [draft, fetchSuggestions]);

  // Click-outside closes the dropdown
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const commitSuggestion = (s) => {
    setChips([...chips, formatChip({ name: s.name, email: s.email })]);
    setDraft("");
    setOpen(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  };
  const commitDraftAsIs = () => {
    const t = draft.trim().replace(/[,;]$/, "");
    if (!t) return;
    setChips([...chips, t]);
    setDraft("");
  };
  const removeChip = (idx) => {
    const next = chips.filter((_, i) => i !== idx);
    setChips(next);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // Iter 70 — Paste-explode. If the user pastes a string containing
  // multiple addresses (Outlook/Excel "a@x.com; b@y.com", or one-per-line
  // from a CSV), split them all into chips at once instead of letting
  // them sit as a single un-validated blob.
  const onPaste = (e) => {
    const pasted = e.clipboardData?.getData("text") || "";
    if (!pasted) return;
    const tokens = explodePastedString(pasted);
    if (tokens.length === 0) return;
    e.preventDefault();
    // If a draft was already in flight, keep it as the first token.
    const merged = [];
    if (draft.trim()) merged.push(draft.trim());
    merged.push(...tokens);
    setChips([...chips, ...merged]);
    setDraft("");
    setOpen(false);
  };

  const onKey = (e) => {
    if (e.key === "Backspace" && !draft && chips.length > 0) {
      // Pop the last chip back into the input for editing.
      const last = chips[chips.length - 1];
      setChips(chips.slice(0, -1));
      setDraft(last);
      return;
    }
    if (open && suggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((i) => Math.min(suggestions.length - 1, i + 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setHighlighted((i) => Math.max(0, i - 1)); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); commitSuggestion(suggestions[highlighted]); return; }
      if (e.key === "Escape") { setOpen(false); return; }
    }
    // Iter 70 — Tab + Space commit the typed token to a chip (in addition
    // to the existing Enter / comma / semicolon). Space-commit only fires
    // if the draft already looks like an email (contains an @) so users
    // can still type "Bhimal Hira" before the "<email>" suffix.
    if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
      if (draft.trim()) { e.preventDefault(); commitDraftAsIs(); }
      return;
    }
    if (e.key === " " || e.key === "Spacebar") {
      const t = draft.trim();
      if (t && t.includes("@")) {
        e.preventDefault();
        commitDraftAsIs();
      }
    }
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div
        className="flex flex-wrap items-center gap-1 min-h-7 cursor-text"
        onClick={() => inputRef.current?.focus()}
        data-testid={dataTestId}
      >
        {chips.map((c, i) => (
          <Chip key={`${c}-${i}`} token={c} onRemove={() => removeChip(i)} testId={`${dataTestId}-chip-${i}`}/>
        ))}
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKey}
          onPaste={onPaste}
          onFocus={() => draft && setOpen(true)}
          onBlur={() => { if (draft.trim()) commitDraftAsIs(); }}
          placeholder={chips.length === 0 ? placeholder : ""}
          autoComplete="off"
          className="flex-1 min-w-[120px] outline-none bg-transparent text-[13px] placeholder:text-slate-400 py-1"
          data-testid={`${dataTestId}-input`}
        />
      </div>
      {open && suggestions.length > 0 && (
        <ul
          className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-y-auto"
          data-testid={`${dataTestId}-suggestions`}
        >
          {suggestions.map((s, i) => (
            <li
              key={s.email}
              onMouseDown={(e) => { e.preventDefault(); commitSuggestion(s); }}
              onMouseEnter={() => setHighlighted(i)}
              data-testid={`addr-suggestion-${i}`}
              className={`px-3 py-1.5 cursor-pointer text-sm flex items-center justify-between gap-2 ${
                i === highlighted ? "bg-blue-50" : ""
              }`}
            >
              <div className="min-w-0">
                <div className="font-medium text-slate-900 truncate">{s.name || s.email}</div>
                <div className="text-xs text-slate-500 truncate">{s.email}</div>
              </div>
              {s.hint && <div className="text-[10px] text-slate-400 shrink-0">{s.hint}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
